/**
 * WhatsAppCardButton
 *
 * Compact WhatsApp CTA used inside listing cards (grid + list view).
 * - Always visible when the dealer has the WhatsApp preference enabled.
 * - Disabled (non-functional) when the dealer is not subscribed or the phone is missing.
 * - Calls stopPropagation to avoid triggering the card link.
 */
import { useEffect, useState } from "react";
import { WhatsappLogo } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useCountry } from "@/hooks/useCountry";
import { getOrCreateSessionId } from "@/services/kpiService";
import { trackContactClick } from "@/utils/contactTracking";
import { captureLead } from "@/utils/leadCapture";
import { toWhatsAppNumber } from "@/utils/contactInfo";

interface WhatsAppCardButtonProps {
  listingId: string;
  listingTitle: string;
  sellerId?: string;
  className?: string;
  size?: "sm" | "default" | "lg" | "icon";
  iconOnly?: boolean;
}

export const WhatsAppCardButton = ({
  listingId,
  listingTitle,
  sellerId,
  className,
  size = "sm",
  iconOnly = false,
}: WhatsAppCardButtonProps) => {
  const { country } = useCountry();
  const [whatsappEnabled, setWhatsappEnabled] = useState<boolean | null>(null);
  const [phone, setPhone] = useState<string | null>(null);
  const [dealerSubscribed, setDealerSubscribed] = useState<boolean | null>(null);

  // Fetch dealer's WhatsApp preference
  useEffect(() => {
    if (!sellerId) {
      // Private / anonymous listings have no dealer profile — WhatsApp stays available
      setWhatsappEnabled(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from("dealer_profiles")
          .select("whatsapp_enabled")
          .eq("user_id", sellerId)
          .maybeSingle();
        if (cancelled) return;
        // Default to enabled if no profile row found (matches table default)
        setWhatsappEnabled(data ? (data as any).whatsapp_enabled !== false : true);
      } catch {
        if (!cancelled) setWhatsappEnabled(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sellerId]);

  // Direct contact only for subscribed dealers; private listings (no dealer profile) stay open.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (sellerId) {
          const { data: dp } = await supabase
            .from("dealer_profiles").select("user_id").eq("user_id", sellerId).maybeSingle();
          if (dp) {
            const { data: sub } = await supabase.rpc("is_user_subscribed", { _uid: sellerId });
            if (cancelled) return;
            if (sub !== true) { setDealerSubscribed(false); setPhone(null); return; }
          }
        }
        const { data: contactData } = await supabase.rpc("get_public_contact_info", {
          _listing_id: listingId,
        });
        if (cancelled) return;
        setDealerSubscribed(true);
        setPhone(contactData?.[0]?.contact_phone ?? null);
      } catch {
        if (!cancelled) { setDealerSubscribed(true); setPhone(null); }
      }
    })();
    return () => { cancelled = true; };
  }, [listingId, sellerId]);

  // Hide entirely if the seller disabled WhatsApp, or if no phone is available
  if (whatsappEnabled === false) return null;
  if (dealerSubscribed !== null && !phone) return null;

  const isDisabled = !phone;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDisabled || !phone) return;

    // Sanitize phone -> international format without leading +/0
    const digits = toWhatsAppNumber(phone, country as string);
    if (!digits) return;

    const listingUrl =
      typeof window !== "undefined"
        ? `${window.location.origin}/listing/${listingId}`
        : "";
    const greetings: Record<string, string> = {
      RO: `Bună ziua! Sunt interesat de anunțul: ${listingTitle}. ${listingUrl}`,
      UK: `Hello! I'm interested in your listing: ${listingTitle}. ${listingUrl}`,
      DE: `Guten Tag! Ich interessiere mich für Ihre Anzeige: ${listingTitle}. ${listingUrl}`,
    };
    const text = greetings[country as string] || greetings.RO;
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;

    trackContactClick({ id: listingId, sellerId }, "whatsapp", "listing_card");
    captureLead({
      listing_id: listingId,
      dealer_id: sellerId || null,
      contact_type: "whatsapp",
      lead_type: "whatsapp_click",
      session_id: getOrCreateSessionId(),
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Button
      type="button"
      size={size}
      onClick={handleClick}
      aria-disabled={isDisabled}
      aria-label="Contact via WhatsApp"
      data-disabled={isDisabled}
      className={cn(
        "gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold",
        "data-[disabled=true]:opacity-45 data-[disabled=true]:cursor-not-allowed",
        className,
      )}
    >
      <WhatsappLogo size={iconOnly ? 18 : 16} weight="fill" />
      {!iconOnly && <span className="text-xs">WhatsApp</span>}
    </Button>
  );
};

export default WhatsAppCardButton;
